import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { useGviconfigurator } from "../hooks/useSiteConfigurator";
import { useSiteMetadata } from "../hooks/useSiteMetadata";

type ContactDetails = {
  international?: string;
  [key: string]: unknown;
};

type CurrentSiteMeta = {
  currency?: string;
  currency_symbol?: string;
  contact_details?: ContactDetails;
  domain?: string;
  [key: string]: unknown;
};

type ThemeContextValue = {
  colorMode: string | undefined;
  localeLanguage: string;
  currentDomain: string;
  currentBrand: string;
  planetDomain: string;
  peopleDomain: string;
  currentSiteMeta: CurrentSiteMeta;
  isLive: boolean;
  site: Record<string, unknown>;
};

export const ThemeContext = React.createContext<ThemeContextValue>({
  colorMode: process.env.GATSBY_DEFAULT_THEME,
  localeLanguage: "",
  currentDomain: "",
  currentBrand: "",
  planetDomain: "",
  peopleDomain: "",
  currentSiteMeta: {},
  isLive: false,
  site: {}
});

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const colorMode = process.env.GATSBY_DEFAULT_THEME;
  const currentBrand = process.env.GATSBY_PROJECT_NAV_TITLE.toLowerCase();
  const localeLanguage =
    process.env.GATSBY_PROJECT_LOCALE_LANGUAGE?.toLowerCase() || "en";

  const site = useSiteMetadata();

  const [isLive, setIsLive] = useState(false); //we are using for now, to determine what pages use new template - its temporary (6 months temporary).

  const configurator = useGviconfigurator();

  const siteMetadataRef = useRef<CurrentSiteMeta | null>(null);

  siteMetadataRef.current = configurator?.world_contact_details[localeLanguage];

  const currentSiteMeta = siteMetadataRef?.current || {};

  const currentDomain = siteMetadataRef?.current?.domain || "gvi.co.uk";

  const planetDomain = `https://www.${siteMetadataRef?.current?.domain || currentDomain}`

  const peopleDomain = `https://people.${siteMetadataRef?.current?.domain || currentDomain}`


  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    const url = new URL(globalThis?.window?.location?.href);

    if (
      !url?.host?.includes("netlify.app") &&
      !url?.host?.includes("gatsbyjs.io") &&
      !url?.host?.includes("gviworld.com") &&
      !url?.host?.includes("localhost")
    ) {
      setIsLive(true);
    }

    /**
     * NOTES: we could this section to preset all the promos to be shown & set each config option as a key of the promo_config object
     *  - suggestion site banner
     *  - site switcher (FUTURE)
     *  - sticky widget (FUTURE)
     *  - blog promo (FUTURE)
     *
     * REMEMBER - we switched the value of the localstorage to (STRING) "show" or "hide" so that its easier to logically deduce when a user has had interaction with the banner
     * **/

    if (globalThis?.window !== undefined) {
      const userDetailsRaw =
        globalThis?.window?.localStorage.getItem("gvx-analytics") || "{}";

      const userDetails = JSON.parse(userDetailsRaw);

      const promoConfig = JSON.parse(
        globalThis?.window?.localStorage.getItem("promo_config") || "{}"
      );

      //get in here only once, before there is a value in the localstorage
      if (!promoConfig?.site_suggestion) {
        //the initial values of the localstorage seem to be empty most times
        let suggest_country = userDetails?.suggest_country?.toLowerCase();
        let site_country = userDetails?.site_country?.toLowerCase();

        if (typeof document !== "undefined") {
          suggest_country =
            processCookieDetails(document, "gvx-analytics-suggest-country") ||
            userDetails?.suggest_country?.toLowerCase() ||
            "";
          site_country =
            processCookieDetails(document, "gvx-analytics-site-country-code") ||
            userDetails?.site_country?.toLowerCase() ||
            "";
        }

        const showBannerFlag =
          suggest_country?.toLowerCase() !== site_country?.toLowerCase();

        globalThis?.window?.localStorage.setItem(
          "promo_config",
          JSON.stringify({
            ...promoConfig,
            site_suggestion: showBannerFlag ? "show" : "hide",
            site_announcement: "show"
          })
        );
      }

      if (!promoConfig?.site_announcement) {
        globalThis?.window?.localStorage.setItem(
          "promo_config",
          JSON.stringify({
            ...promoConfig,
            site_announcement: "show"
          })
        );
      }

    }
  }, []);

  //process traffic source
  const themeContextValue = useMemo(
    () => ({
      colorMode,
      currentDomain,
      currentSiteMeta,
      currentBrand,
      planetDomain,
      peopleDomain,
      site,
      localeLanguage,
      isLive,
    }),
    [
      colorMode,
      currentDomain,
      currentSiteMeta,
      currentBrand,
      planetDomain,
      peopleDomain,
      site,
      localeLanguage,
      isLive,
    ]
  );

  return (
    <ThemeContext.Provider value={themeContextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Notes: must be run in a useEffect, otherwise it will not work
 * process traffic source
 *  **/

export const processTrafficSource = () => { };
export const processCookieDetails = (document, cookieName) => {
  let cookieArray = document.cookie.split(";");
  for (const element of cookieArray) {
    let cookiePair = element.trim().split("=");
    if (cookiePair[0] === cookieName) {
      return decodeURIComponent(cookiePair[1]);
    }
  }
  return null;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme should be used in ThemeContext provider");
  }

  return context;
};
